/**
 * Where the API answers. A LEAF module on purpose — it imports nothing.
 *
 * This used to live in `api.ts`, which cannot be imported by `telemetry.ts`
 * because `api.ts` imports the tracing helpers FROM `telemetry.ts`. Faced with
 * that cycle, telemetry declared its own `const API_BASE = '/v1'` — and in
 * every environment where the API is a separate host (which is every deployed
 * environment; prod is `https://api.platform.fias.io/v1`) that pointed every
 * error report at the frontend's own CloudFront origin.
 *
 * The SPA fallback answers `/v1/telemetry/errors` with `200 text/html`, so
 * `fetch` resolved, nothing threw, `catch {}` never fired, and the platform
 * reported zero frontend errors for months while looking instrumented. Pulling
 * the constant down here breaks the cycle instead of working around it, so
 * there is one answer to "where is the API" for the whole client.
 *
 * Pinned by `client/src/lib/api-base.test.ts`.
 */

// Use VITE_API_URL for production (separate API subdomain)
// Fall back to /v1 for local development (proxied by Vite)
//
// Codespaces override: when the browser is on *.app.github.dev, the user's
// laptop can't reach the Codespace's `localhost:4000`. Force relative URLs so
// Vite's dev-server proxy (see vite.config.ts) forwards the call server-side.
// Runs at runtime so a .env.local with VITE_API_URL=http://localhost:4000/v1
// stays correct on WSL/native dev where browser and API share localhost.
//
// Exported because `WEBSOCKET_ENDPOINT` in api.ts needs the same answer for the
// same reason (the WS service is on 4001 inside the Codespace).
export const isCodespacesHost =
  typeof window !== 'undefined' && window.location.hostname.endsWith('.app.github.dev');

export const API_BASE = isCodespacesHost ? '/v1' : import.meta.env.VITE_API_URL || '/v1';

/**
 * The origin the API answers from, resolved from `API_BASE`.
 *
 * `API_BASE` is either relative (`/v1`, API shares the frontend origin) or
 * absolute (`https://api.<env>.fias.io/v1`, separate subdomain), so this
 * normalises both. Used to verify `postMessage` senders: the OAuth popup is
 * served BY THE API HOST and posts to its opener on the frontend host, so a
 * receiver must accept that origin and no other.
 */
export const API_ORIGIN: string = (() => {
  if (typeof window === 'undefined') return '';
  try {
    return new URL(API_BASE, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
})();
